import { Section } from '@factorialco/deprecated-design-system'
import {
  BulkAssignProjectRoleDocument,
  BulkAssignProjectRoleMutation,
  ProjectDetailsPageSettingsPageRolesSectionFragment,
  ProjectManagementProjectDetailsPageSettingsRolesSectionUpdateRoleDocument,
  ProjectManagementProjectDetailsPageSettingsRolesSectionUpdateRoleMutation,
  ProjectManagementProjectRoleRoleEnum,
  ProjectRolesDocument,
  UnassignProjectRoleDocument,
  UnassignProjectRoleMutation,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import i18n from 'modules/core/lib/i18n'

import { RolesSelector } from 'modules/project_management/components/RolesSelector'

interface RolesSectionProps {
  project: ProjectDetailsPageSettingsPageRolesSectionFragment
  currentEmployeeId: number
}

export const RolesSection: React.FC<RolesSectionProps> = ({ project, currentEmployeeId }) => {
  const [unassign] = useMutation(UnassignProjectRoleDocument, {
    extractorFn: (data: UnassignProjectRoleMutation) =>
      data.projectManagementMutations.unassignProjectmanagementProjectRole,
  })

  const [bulkAssignProjectRole] = useMutation(BulkAssignProjectRoleDocument, {
    extractorFn: (data: BulkAssignProjectRoleMutation) =>
      data.projectManagementMutations.bulkAssignProjectmanagementProjectRole,
  })

  const [update] = useMutation(
    ProjectManagementProjectDetailsPageSettingsRolesSectionUpdateRoleDocument,
    {
      extractorFn: (
        data: ProjectManagementProjectDetailsPageSettingsRolesSectionUpdateRoleMutation
      ) => data.projectManagementMutations.updateProjectmanagementProjectRole,
    }
  )

  const onUpdateRow = (projectRoleId: number, role: ProjectManagementProjectRoleRoleEnum) => {
    update({
      variables: { id: projectRoleId, role },
      optimisticResponse: {
        projectManagementMutations: {
          updateProjectmanagementProjectRole: {
            errors: [],
            projectRole: {
              __typename: 'ProjectManagementProjectRole',
              id: projectRoleId,
              role,
            },
          },
        },
      },
    })
  }

  const onCreateRow = (owners: number[], editors: number[], viewers: number[]) => {
    bulkAssignProjectRole({
      variables: { owners, editors, viewers, projectIds: [project.id] },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: ProjectRolesDocument,
          variables: {
            projectIds: [project.id],
          },
        },
      ],
    })
  }

  const onDeleteRow = async (projectRoleId: number) => {
    await unassign({
      variables: { projectRoleId },
      update(cache) {
        cache.evict({
          id: cache.identify({
            id: projectRoleId,
            __typename: 'ProjectManagementProjectRole',
          }),
        })
      },
    })
  }

  const canAssignOwners = project._policies?.assignOwner.can ?? false
  const canAssignEditors = project._policies?.assignEditor.can ?? false

  return (
    <Section
      illustration="add-user"
      title={i18n.t('project_management.settings.sections.project_roles.title')}
      description={i18n.t('project_management.settings.sections.project_roles.description')}
      paddingX="s0"
    >
      <RolesSelector
        projectId={project.id}
        onCreateRow={onCreateRow}
        onUpdateRow={onUpdateRow}
        onDeleteRow={onDeleteRow}
        useModal
        canAssignOwners={canAssignOwners}
        canAssignEditors={canAssignEditors}
        currentEmployeeId={currentEmployeeId}
      />
    </Section>
  )
}
